import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { SITE, NAV_SERVICES } from "@/data/site";

const Logo = () => (
  <Link to="/" className="inline-flex items-center" data-testid="header-logo" aria-label="HRC Rénovation — Accueil">
    <img src="/logo-hrc.png" alt="Logo HRC Rénovation" className="h-12 w-12 rounded-xl object-cover" width="48" height="48" />
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const linkCls = ({ isActive }) =>
    `text-[15px] font-medium transition-colors ${isActive ? "text-brand-royal" : "text-ink hover:text-brand-royal"}`;

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 px-3 sm:px-5 pt-3" data-testid="site-header">
        <div
          className={`max-w-site mx-auto bg-white rounded-full px-4 sm:px-6 h-[68px] flex items-center justify-between transition-shadow duration-200 ${scrolled || mobileOpen ? "shadow-elevated" : "shadow-subtle"}`}
        >
          {/* Mobile : hamburger à gauche */}
          <button
            type="button"
            className="lg:hidden p-2 -ml-2 text-brand-deep"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <Logo />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Navigation principale">
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                type="button"
                className="flex items-center gap-1 text-[15px] font-medium text-ink hover:text-brand-royal transition-colors py-4"
                aria-expanded={servicesOpen}
                data-testid="nav-services-dropdown"
                onClick={() => setServicesOpen(!servicesOpen)}
              >
                Services
                <ChevronDown size={15} className={`transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 bg-white rounded-2xl shadow-elevated border border-line p-2 w-64">
                  {NAV_SERVICES.map((s) => (
                    <Link
                      key={s.path}
                      to={s.path}
                      className="block px-4 py-3 rounded-xl text-[15px] font-medium text-ink hover:bg-brand-highlight hover:text-brand-royal transition-colors"
                      data-testid={`nav-service-${s.path.slice(1)}`}
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <NavLink to="/zones-intervention" className={linkCls} data-testid="nav-zones">Zones</NavLink>
            <NavLink to="/a-propos" className={linkCls} data-testid="nav-apropos">À propos</NavLink>
            <NavLink to="/contact" className={linkCls} data-testid="nav-contact">Contact</NavLink>
          </nav>

          {/* Droite */}
          <a
            href={SITE.phoneHref}
            className="!hidden lg:!inline-flex btn-base btn-metal !py-3 !px-6 !text-[15px] tnum"
            data-testid="header-phone-btn"
          >
            <Phone size={17} aria-hidden="true" />
            {SITE.phone}
          </a>
          <a href={SITE.phoneHref} className="lg:hidden p-2 -mr-2 text-brand-royal" aria-label={`Appeler le ${SITE.phone}`} data-testid="header-phone-mobile">
            <Phone size={22} />
          </a>
        </div>
      </header>

      {/* Drawer mobile plein écran */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-white pt-[100px] px-8 lg:hidden overflow-y-auto" data-testid="mobile-menu-drawer">
          <nav className="flex flex-col gap-2" aria-label="Menu mobile">
            <p className="micro-label mt-4 mb-2">Services</p>
            {NAV_SERVICES.map((s) => (
              <Link key={s.path} to={s.path} className="font-heading font-semibold text-2xl text-brand-deep py-2">
                {s.label}
              </Link>
            ))}
            <div className="h-px bg-line my-4" />
            <Link to="/zones-intervention" className="font-heading font-semibold text-2xl text-brand-deep py-2">Zones d'intervention</Link>
            <Link to="/a-propos" className="font-heading font-semibold text-2xl text-brand-deep py-2">À propos</Link>
            <Link to="/contact" className="font-heading font-semibold text-2xl text-brand-deep py-2">Contact</Link>
            <Link to="/devis" className="btn-base btn-metal mt-6 w-full" data-testid="mobile-menu-devis-btn">
              Obtenir mon devis gratuit
            </Link>
            <a href={SITE.phoneHref} className="btn-base btn-outline-deep mt-3 w-full tnum">
              <Phone size={18} aria-hidden="true" />
              {SITE.phone}
            </a>
          </nav>
        </div>
      )}
    </>
  );
};
